import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'next-i18next/pages';
import { PackageCode } from 'api/queries/packageTour';
import FieldTrigger, {
  FieldVariant,
  fieldWrapperClassName,
  panelPositionClassName,
} from './FieldTrigger';

interface IBoardTypeDropdownProps {
  value: string;
  options: PackageCode[];
  onChange: (v: string) => void;
  variant?: FieldVariant;
}

const BoardTypeDropdown: React.FC<IBoardTypeDropdownProps> = ({
  value,
  options,
  onChange,
  variant = 'field',
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  // '' is "any"; usePackageReferenceData translates the option names.
  const all = [{ code: '', name: t('Any') }, ...options];
  // Without the reference data a chosen board still shows, by its code
  // (WHIT-526).
  const chosen = options.find(b => b.code === value)?.name || value;
  // In the settings row "Any" alone would not say what it is about.
  const label =
    chosen || (variant === 'setting' ? t('Board type') : t('Any'));

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className={fieldWrapperClassName(variant)}>
      <FieldTrigger
        label={t('Board type')}
        showLabel
        variant={variant}
        open={open}
        filled={!!chosen}
        onClick={() => setOpen(o => !o)}>
        <span className="text-black">{label}</span>
      </FieldTrigger>

      {open && (
        <div
          className={`absolute z-50 top-full mt-1 ${panelPositionClassName(
            variant,
          )} min-w-[220px] bg-white rounded-xl shadow-lg border border-tt-gray-purple`}>
          <div className="py-1">
            {all.map(b => (
              <button
                key={b.code}
                type="button"
                onClick={() => {
                  onChange(b.code);
                  setOpen(false);
                }}
                className={`w-full text-left px-4 py-2.5 text-sm hover:bg-tt-verylight-purple transition-colors flex items-center gap-2 ${
                  value === b.code ? 'font-semibold text-tt-dark-purple' : ''
                }`}
              >
                {value === b.code && <span className="text-tt-yellow">✓</span>}
                {b.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BoardTypeDropdown;
